import React, { useEffect, useRef, useState } from "react";
import { Cartridge, Disposable, LiquidBottle, PodDevice, SharedDefs } from "./products";
import { startSplashEngine } from "./splashEngine";
import "./SplashScreen.css";

const FADE_MS = 650;

const CAPTIONS = ["Заряжаем устройства", "Смешиваем вкусы", "Проверяем наличие"];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Повноекранний екран завантаження. Коли `visible` стає false —
 * прогрес добігає до 100%, і екран плавно зникає.
 */
const SplashScreen = ({ visible, expectedMs = 3500, onShowcaseDone }) => {
  const [mounted, setMounted] = useState(visible);
  // Через ref, щоб новий колбек на кожному рендері не перезапускав рушій
  const onDoneRef = useRef(onShowcaseDone);
  onDoneRef.current = onShowcaseDone;

  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const podPercentRef = useRef(null);
  const podBatteryRef = useRef(null);
  const percentRef = useRef(null);
  const barRef = useRef(null);
  const engineRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      return undefined;
    }
    engineRef.current?.finish();
    const t = setTimeout(() => setMounted(false), FADE_MS);
    return () => clearTimeout(t);
  }, [visible]);

  useEffect(() => {
    if (!mounted) return undefined;

    const engine = startSplashEngine({
      root: rootRef.current,
      canvas: canvasRef.current,
      reducedMotion: prefersReducedMotion(),
      expectedMs,
      onDone: () => onDoneRef.current?.(),
      emitters: [
        // Перші «затяжки» — після того, як девайси впали й стали (анімація sp-drop)
        { id: "pod", offset: 1300, duration: 1300 },
        { id: "disposable", offset: 2900, duration: 950 },
      ],
      // Оновлюємо DOM напряму, без ре-рендеру React на кожен відсоток
      onProgress: (p) => {
        if (podPercentRef.current) podPercentRef.current.textContent = `${p}%`;
        if (podBatteryRef.current) podBatteryRef.current.setAttribute("width", (6.8 * p) / 100);
        if (percentRef.current) percentRef.current.textContent = `${p}%`;
        if (barRef.current) barRef.current.style.transform = `scaleX(${p / 100})`;
      },
    });
    engineRef.current = engine;

    return () => {
      engine.stop();
      engineRef.current = null;
    };
  }, [mounted, expectedMs]);

  if (!mounted) return null;

  return (
    <div
      ref={rootRef}
      className={`splash ${visible ? "" : "splash--hidden"}`}
      role="status"
      aria-live="polite"
      aria-label="Загрузка"
    >
      <SharedDefs />

      <div className="sp-fit">
        <div className="sp-scene">
          <div className="sp-aura sp-aura--violet" />
          <div className="sp-aura sp-aura--green" />
          <div className="sp-aura sp-aura--pink" />
          <div className="sp-floor" />

          <div className="sp-stage">
            <div className="sp-item sp-item--disposable">
              <Disposable />
            </div>

            <div className="sp-item sp-item--pod">
              <PodDevice percentRef={podPercentRef} batteryRef={podBatteryRef} />
            </div>

            <div className="sp-item sp-item--bottle">
              <LiquidBottle />
            </div>

            <div className="sp-cart-shadow" />
            <div className="sp-item sp-item--cartridge">
              <Cartridge />
            </div>
          </div>
        </div>
      </div>

      <canvas ref={canvasRef} className="sp-canvas" aria-hidden="true" />

      <div className="sp-text">
        <div className="sp-brand">SMAK.BAR</div>
        <div className="sp-tagline">Поды · Жидкости · Картриджи</div>

        <div className="sp-status">
          <div className="sp-captions">
            {CAPTIONS.map((c, i) => (
              <span key={c} className="sp-caption" style={{ animationDelay: `${i * 2.5}s` }}>
                {c}
              </span>
            ))}
          </div>
          <span ref={percentRef} className="sp-percent">
            0%
          </span>
        </div>
        <div className="sp-progress">
          <span ref={barRef} className="sp-progress-fill" />
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
